import React, { createContext, useContext, useEffect } from 'react';

export interface AcademyBranding {
  primaryColor?: string;
  secondaryColor?: string;
  /** True when inside AcademyBrandingProvider (e.g. student/guardian dashboard) */
  isAcademyBranded?: boolean;
}

const DEFAULT_PRIMARY = '#2563eb';
const DEFAULT_SECONDARY = '#1d4ed8';

const AcademyBrandingContext = createContext<AcademyBranding>({});

export function useAcademyBranding(): AcademyBranding {
  return useContext(AcademyBrandingContext);
}

/** Hex color for primary - falls back to default when not set */
export function useAcademyPrimaryColor(): string {
  const { primaryColor } = useAcademyBranding();
  return primaryColor || DEFAULT_PRIMARY;
}

/** Hex color for secondary - falls back to default when not set */
export function useAcademySecondaryColor(): string {
  const { secondaryColor } = useAcademyBranding();
  return secondaryColor || DEFAULT_SECONDARY;
}

interface AcademyBrandingProviderProps {
  primaryColor?: string;
  secondaryColor?: string;
  children: React.ReactNode;
}

/**
 * Provides academy branding colors to children. When academy colors are set,
 * wrap the page/layout with this provider so components can use them.
 * Sets CSS variables --academy-primary and --academy-secondary for styling.
 */
export function AcademyBrandingProvider({
  primaryColor,
  secondaryColor,
  children,
}: AcademyBrandingProviderProps) {
  const primary = primaryColor || DEFAULT_PRIMARY;
  const secondary = secondaryColor || DEFAULT_SECONDARY;
  const value: AcademyBranding = {
    primaryColor: primaryColor || undefined,
    secondaryColor: secondaryColor || undefined,
    isAcademyBranded: true,
  };

  // Apply branding to body so portaled content (Dialogs, Popovers, etc.) inherits tenant colors
  useEffect(() => {
    const style = document.body.style;
    style.setProperty('--tenant-primary', primary);
    style.setProperty('--tenant-secondary', secondary);
    style.setProperty('--academy-primary', primary);
    style.setProperty('--academy-secondary', secondary);
    style.setProperty('--color-primary-100', 'color-mix(in srgb, var(--academy-primary) 15%, white)');
    style.setProperty('--color-primary-300', 'color-mix(in srgb, var(--academy-primary) 50%, white)');
    style.setProperty('--color-primary-500', primary);
    style.setProperty('--color-primary-600', primary);
    style.setProperty('--color-primary-700', primary);
    style.setProperty('--color-primary-800', 'color-mix(in srgb, var(--academy-primary) 80%, black)');
    style.setProperty('--color-primary-900', 'color-mix(in srgb, var(--academy-primary) 60%, black)');
    return () => {
      style.removeProperty('--tenant-primary');
      style.removeProperty('--tenant-secondary');
      style.removeProperty('--academy-primary');
      style.removeProperty('--academy-secondary');
      style.removeProperty('--color-primary-100');
      style.removeProperty('--color-primary-300');
      style.removeProperty('--color-primary-500');
      style.removeProperty('--color-primary-600');
      style.removeProperty('--color-primary-700');
      style.removeProperty('--color-primary-800');
      style.removeProperty('--color-primary-900');
    };
  }, [primary, secondary]);

  return (
    <AcademyBrandingContext.Provider value={value}>
      <div
        className="academy-branding"
        data-academy-branding
        style={
          {
            '--academy-primary': primary,
            '--academy-secondary': secondary,
            /* Override Tailwind primary palette so bg-primary-*, text-primary-* use academy color */
            '--color-primary-100': 'color-mix(in srgb, var(--academy-primary) 15%, white)',
            '--color-primary-300': 'color-mix(in srgb, var(--academy-primary) 50%, white)',
            '--color-primary-500': primary,
            '--color-primary-600': primary,
            '--color-primary-700': primary,
            '--color-primary-800': 'color-mix(in srgb, var(--academy-primary) 80%, black)',
            '--color-primary-900': 'color-mix(in srgb, var(--academy-primary) 60%, black)',
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </AcademyBrandingContext.Provider>
  );
}

/** Alias for tenant-scoped public branding (clubs + academies). */
export const TenantBrandingProvider = AcademyBrandingProvider;

/** Hex color for primary tenant branding */
export function useTenantPrimaryColor(): string {
  return useAcademyPrimaryColor();
}

export function useTenantSecondaryColor(): string {
  return useAcademySecondaryColor();
}
